import React, { useEffect, useRef, useState } from 'react';
import ChatBubble from '../Components/Chatbubble';
import MessageInput from '../Components/MessageInput';
import aiAvatar from '../Assets/kumar-avatar.png';
import userAvatar from '../Assets/User-avatar.png';
import TypingDots from '../Components/TypingDots';
import { callOpenAI } from '../utils/openaiService';

const RecommendedSkills = () => {
  const chatContainerRef = useRef(null);
  const bottomRef = useRef(null);

  const [chatMessages, setChatMessages] = useState([
    {
      sender: 'ai',
      avatar: aiAvatar,
      name: 'Kumar',
      message: `Let’s upgrade your skill stack! 🚀\nWhat career are you aiming for right now?`,
      timestamp: new Date().toLocaleString(),
    },
  ]);
  const [recommendedSkills, setRecommendedSkills] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [showCards, setShowCards] = useState(false);
  const [lastCareer, setLastCareer] = useState('');
  const [awaitingNext, setAwaitingNext] = useState(false);

  // auto-scroll whenever chatMessages, showCards or awaitingNext changes
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, showCards, awaitingNext]);

  const formatMessages = (msgs) =>
    msgs.map((m) => ({ role: m.sender === 'ai' ? 'assistant' : 'user', content: m.message }));

  const addMessage = (msg) =>
    setChatMessages((prev) => [...prev, { ...msg, timestamp: new Date().toLocaleString() }]);

  const handleUserInput = async (text) => {
    if (!text.trim()) return;
    addMessage({ sender: 'user', avatar: userAvatar, name: 'You', message: text });

    // if waiting for “yes” to show cards again
    if (awaitingNext) {
      if (text.toLowerCase() === 'yes') {
        setShowCards(true);
        setAwaitingNext(false);
        addMessage({ sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: `Here are the skills again—pick one to learn more.` });
      } else {
        addMessage({ sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: `Okay! Let me know whenever you're ready for more skills.` });
      }
      return;
    }

    // initial career input
    if (!showCards && !lastCareer) {
      setLastCareer(text);
      setIsThinking(true);
      const skillListResponse = await callOpenAI({
        messages: [
          ...formatMessages(chatMessages),
          { role: 'user', content: `List 4 useful skills for someone becoming a ${text}. Include a short “Why it matters” and “Use case” for each.` },
        ],
        screenContext: 'Recommended skills',
      });
      setIsThinking(false);

      const blocks = skillListResponse.split('\n\n').filter((b) => b.includes('Why it matters'));
      const skills = blocks.slice(0, 4).map((block) => {
        const [titleLine, whyLine, useLine] = block.split('\n');
        return {
          title: titleLine.replace(/^\d+\.\s*/, '').trim(),
          why: whyLine.replace('Why it matters:', '').trim(),
          use: useLine.replace('Use case:', '').trim(),
        };
      });

      setRecommendedSkills(skills);
      setShowCards(true);
      addMessage({ sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: `Great! Based on that, here are 4 skills. Pick one to “Start to Learn.”` });
      return;
    }

    // any other typed input is ignored until cards clicked or next prompt
  };

  const handleSkillClick = async (skill) => {
    setShowCards(false);
    setIsThinking(true);

    const detail = await callOpenAI({
      messages: [
        ...formatMessages(chatMessages),
        { role: 'user', content: `Explain "${skill.title}" in depth: why it matters, use cases, and how to learn it.` },
      ],
      screenContext: 'Recommended skills',
    });
    setIsThinking(false);

    addMessage({ sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: detail });
    addMessage({ sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: `Shall we move to the next skill? (Yes/No)` });
    setAwaitingNext(true);
  };

  return (
    <div className="w-full max-w-[1000px] mx-auto p-6 flex flex-col justify-between bg-white/80 rounded-[30px] shadow-xl backdrop-blur-md min-h-[75vh] max-h-[90vh]">
      <div ref={chatContainerRef} className="flex flex-col gap-6 overflow-y-auto flex-grow pr-2">
        {/* chat bubbles first */}
        {chatMessages.map((m, i) => (
          <ChatBubble key={i} {...m} />
        ))}

        {isThinking && <ChatBubble sender="ai" avatar={aiAvatar} name="Kumar" message={<TypingDots />} />}

        {/* then skill cards when flag is true */}
        {showCards && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            {recommendedSkills.map((skill, idx) => (
              <div key={idx} className="p-4 bg-[#DED1CF]/40 rounded-2xl flex flex-col justify-between">
                <div>
                  <div className="text-lg font-bold">{skill.title}</div>
                  <div className="mt-1 font-semibold">⚡ Why it matters?</div>
                  <div className="text-sm">{skill.why}</div>
                  <div className="mt-2 font-semibold">🎯 Use case:</div>
                  <div className="text-sm">{skill.use}</div>
                </div>
                <button
                  className="mt-3 bg-[#FF5733] text-white py-2 rounded-lg"
                  onClick={() => handleSkillClick(skill)}
                >
                  Start to Learn
                </button>
              </div>
            ))}
          </div>
        )}

        {/* bottom sentinel */}
        <div ref={bottomRef} />
      </div>

      <div className="mt-6">
        <MessageInput onSend={handleUserInput} disabled={isThinking} />
      </div>
    </div>
  );
};

export default RecommendedSkills;
